import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
  InputBase,
  useMediaQuery,
} from "@mui/material";
import { Add, ChatBubbleOutline, ConfirmationNumberOutlined, History } from "@mui/icons-material";
import { useAuth } from "../../contexts/AuthContext";
import { ActionChips, AuditNotice, ComposerFrame, SendButton, SpeakerLabel } from "../assistants/AssistantUi";
import { colors } from "../../theme/designSystem";
import { formatWhen, parseAssistant, renderMarkdown } from "./AgentConversation";
import {
  SupportAgentInfo,
  SupportConversation as SupportConversationSummary,
  SupportMessage,
  SupportTicketCategory,
  SupportTicketDetail,
  SupportTicketSummary,
  supportService,
  TICKET_CATEGORY_LABEL,
  TICKET_STATUS_LABEL,
} from "../../services/supportService";

// Painel do contato "Suporte Clinic4Us" no chat: conversa com o assistente da plataforma e os chamados do
// usuário. Abre direto numa conversa nova (o usuário leigo não precisa escolher nada para começar).

type View = "chat" | "history" | "tickets" | "ticket" | "newTicket";

export interface SupportConversationHandle {
  /** Volta um nível dentro do suporte; devolve false quando já está no início (a casca decide o resto). */
  back: () => boolean;
  startNew: () => void;
}

interface Props {
  info: SupportAgentInfo;
  currentPage: string;
  /** Chamados com resposta nova — a casca soma no badge do chat. */
  onUnreadTickets?: (count: number) => void;
}

const STATUS_COLOR: Record<string, { bg: string; fg: string }> = {
  open: { bg: "#fff4e5", fg: "#8a5300" },
  in_progress: { bg: "#e7f1ff", fg: "#1c4f9c" },
  waiting_customer: { bg: "#f3f0ff", fg: "#5f3dc4" },
  resolved: { bg: "#e9f7ef", fg: "#1e7b45" },
};

const errorMessage = (e: unknown, fallback: string): string =>
  (e as { response?: { data?: { description?: string } } })?.response?.data?.description || fallback;

const SupportConversation = forwardRef<SupportConversationHandle, Props>(({ info, currentPage, onUnreadTickets }, ref) => {
  const isMobile = useMediaQuery("(max-width:767.98px)");
  const { user } = useAuth();
  const userInitial = (user?.email || "V").trim().charAt(0).toUpperCase();
  const [view, setView] = useState<View>("chat");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [history, setHistory] = useState<SupportConversationSummary[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openedTicket, setOpenedTicket] = useState<number | null>(null);

  const [tickets, setTickets] = useState<SupportTicketSummary[]>([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticket, setTicket] = useState<SupportTicketDetail | null>(null);
  const [reply, setReply] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [newCategory, setNewCategory] = useState<SupportTicketCategory>("question");
  const [newDescription, setNewDescription] = useState("");

  const listEndRef = useRef<HTMLDivElement | null>(null);
  const lastMsgRef = useRef<HTMLDivElement | null>(null);

  const unreadCount = tickets.filter((t) => t.unread).length;

  useEffect(() => {
    if (view !== "chat") return;
    if (!sending && lastMsgRef.current && messages.length > 0 && messages[messages.length - 1].role === "assistant") {
      lastMsgRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      listEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [messages, sending, view]);

  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const list = await supportService.tickets();
      setTickets(list);
      onUnreadTickets?.(list.filter((t) => t.unread).length);
    } catch {
      setTickets([]);
    } finally {
      setTicketsLoading(false);
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openTickets = () => {
    setView("tickets");
    loadTickets();
  };

  const startNew = () => {
    setConversationId(null);
    setMessages([]);
    setError(null);
    setOpenedTicket(null);
    setView("chat");
  };

  const openHistory = async () => {
    setView("history");
    try {
      setHistory(await supportService.conversations(info.id));
    } catch {
      setHistory([]);
    }
  };

  const openConversation = async (id: string) => {
    setConversationId(id);
    setMessages([]);
    setError(null);
    setOpenedTicket(null);
    setView("chat");
    try {
      setMessages(await supportService.messages(id));
    } catch {
      setError("Não foi possível carregar a conversa.");
    }
  };

  const openTicket = async (id: string) => {
    setView("ticket");
    setTicket(null);
    setReply("");
    setError(null);
    try {
      const detail = await supportService.ticket(id);
      setTicket(detail);
      setTickets((prev) => {
        const next = prev.map((t) => (t.id === id ? { ...t, unread: false } : t));
        onUnreadTickets?.(next.filter((t) => t.unread).length);
        return next;
      });
    } catch {
      setError("Não foi possível abrir o chamado.");
    }
  };

  useImperativeHandle(ref, () => ({
    startNew,
    back: () => {
      if (view === "ticket" || view === "newTicket") {
        setView("tickets");
        return true;
      }
      if (view === "history" || view === "tickets") {
        setView("chat");
        return true;
      }
      return false;
    },
  }));

  const send = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || sending) return;
    if (raw === undefined) setInput("");
    setError(null);
    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "user", content: text, createdAt: new Date().toISOString(), helpful: null },
    ]);
    setSending(true);
    try {
      const result = await supportService.send(info.id, conversationId, text, currentPage || null);
      setConversationId(result.conversationId);
      setMessages((prev) => [...prev, result.reply]);
      if (result.ticketNumber) {
        setOpenedTicket(result.ticketNumber);
        loadTickets();
      }
    } catch (e) {
      setError(errorMessage(e, "Não foi possível enviar a mensagem."));
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    const body = reply.trim();
    if (!body || !ticket || sending) return;
    setSending(true);
    setError(null);
    try {
      const message = await supportService.reply(ticket.ticket.id, body);
      setTicket({ ...ticket, messages: [...ticket.messages, message] });
      setReply("");
      loadTickets();
    } catch (e) {
      setError(errorMessage(e, "Não foi possível enviar a resposta."));
    } finally {
      setSending(false);
    }
  };

  const createTicket = async () => {
    if (!newSubject.trim() || !newDescription.trim() || sending) return;
    setSending(true);
    setError(null);
    try {
      const created = await supportService.openTicket(newSubject.trim(), newDescription.trim(), newCategory, currentPage || null);
      setNewSubject("");
      setNewDescription("");
      setNewCategory("question");
      await loadTickets();
      openTicket(created.id);
    } catch (e) {
      setError(errorMessage(e, "Não foi possível abrir o chamado."));
    } finally {
      setSending(false);
    }
  };

  const tabs = (
    <Box sx={{ display: "flex", gap: 0.5, px: 1, pt: 1, pb: 0.5, borderBottom: "1px solid #e9ecef", bgcolor: "#fff" }}>
      {[
        { key: "chat", label: "Conversa", active: view === "chat" || view === "history" },
        {
          key: "tickets",
          label: unreadCount > 0 ? `Meus chamados (${unreadCount})` : "Meus chamados",
          active: view === "tickets" || view === "ticket" || view === "newTicket",
        },
      ].map((t) => (
        <Button
          key={t.key}
          size="small"
          onClick={() => (t.key === "chat" ? setView("chat") : openTickets())}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.82rem",
            borderRadius: "16px",
            px: 1.5,
            color: t.active ? "#fff" : colors.textSecondary,
            bgcolor: t.active ? colors.primary : "transparent",
            "&:hover": { bgcolor: t.active ? "#029AAB" : "#f1f3f5" },
          }}
        >
          {t.label}
        </Button>
      ))}
    </Box>
  );

  const statusChip = (status: string) => (
    <Chip
      label={TICKET_STATUS_LABEL[status as keyof typeof TICKET_STATUS_LABEL] ?? status}
      size="small"
      sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: STATUS_COLOR[status]?.bg, color: STATUS_COLOR[status]?.fg }}
    />
  );

  const renderAssistant = (content: string, isLast: boolean) => {
    const { text, actions } = parseAssistant(content);
    return (
      <>
        {renderMarkdown(text)}
        {isLast && <ActionChips actions={actions} onPick={(a) => send(a)} isMobile={isMobile} disabled={sending} />}
      </>
    );
  };

  // Molde dos assistentes (linguagem única, 28/10): sem balão — quem fala numa linha, o texto embaixo
  const bubble = (role: string, children: React.ReactNode, key: React.Key, refProp?: React.Ref<HTMLDivElement>, name?: string) => (
    <Box key={key} ref={refProp}>
      <SpeakerLabel isUser={role === "user"} initial={userInitial} name={name ?? (role === "user" ? "Você" : info.displayName)} />
      <Box sx={{ fontSize: "0.9375rem", lineHeight: 1.55, color: colors.textStrong, wordBreak: "break-word" }}>{children}</Box>
    </Box>
  );

  const composer = (
    value: string,
    onChange: (v: string) => void,
    onSend: () => void,
    placeholder: string
  ) => (
    <Box sx={{ px: 1.5, pt: 1, pb: 1.5, bgcolor: colors.white }}>
      <ComposerFrame isMobile={isMobile} disabled={sending}>
        <InputBase
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder={placeholder}
          fullWidth
          multiline
          maxRows={4}
          disabled={sending}
          inputProps={{ maxLength: 2000, "aria-label": placeholder }}
          sx={{ fontSize: "0.9375rem", lineHeight: 1.45, color: colors.textStrong, py: 0.5, pl: 1, "& textarea::placeholder": { color: colors.textSecondary, opacity: 1 } }}
        />
        <SendButton onClick={onSend} disabled={sending || !value.trim()} isMobile={isMobile} />
      </ComposerFrame>
    </Box>
  );

  // ---------- Conversa ----------
  if (view === "chat") {
    return (
      <>
        {tabs}
        <AuditNotice clinicName={user?.clinicName} />
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5, bgcolor: colors.white, display: "flex", flexDirection: "column", gap: 2.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button size="small" startIcon={<History fontSize="small" />} onClick={openHistory} sx={{ textTransform: "none", color: colors.textSecondary }}>
              Conversas anteriores
            </Button>
            {messages.length > 0 && (
              <Button size="small" startIcon={<Add fontSize="small" />} onClick={startNew} sx={{ textTransform: "none", color: colors.primary }}>
                Nova conversa
              </Button>
            )}
          </Box>
          {messages.length === 0 && info.welcomeMessage && bubble("assistant", renderMarkdown(info.welcomeMessage), "welcome")}
          {messages.map((m, i) =>
            bubble(
              m.role,
              m.role === "assistant" ? renderAssistant(m.content, i === messages.length - 1) : <Box sx={{ whiteSpace: "pre-wrap" }}>{m.content}</Box>,
              m.id,
              i === messages.length - 1 ? lastMsgRef : undefined
            )
          )}
          {openedTicket && (
            <Box
              role="status"
              sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: "10px", bgcolor: "#e9f7ef", border: "1px solid #b7e1c7" }}
            >
              <ConfirmationNumberOutlined sx={{ color: "#1e7b45" }} fontSize="small" />
              <Typography sx={{ fontSize: "0.82rem", color: "#1e7b45", flex: 1 }}>
                Chamado <strong>#{openedTicket}</strong> aberto. Acompanhe em Meus chamados.
              </Typography>
              <Button size="small" onClick={openTickets} sx={{ textTransform: "none", color: "#1e7b45", fontWeight: 600 }}>
                Ver
              </Button>
            </Box>
          )}
          {sending && (
            <Box sx={{ alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 1, color: colors.textSecondary }}>
              <CircularProgress size={14} sx={{ color: colors.primary }} />
              <Typography variant="caption">digitando…</Typography>
            </Box>
          )}
          {error && (
            <Typography variant="caption" sx={{ alignSelf: "center", textAlign: "center", color: colors.error }}>
              {error}
            </Typography>
          )}
          <div ref={listEndRef} />
        </Box>
        {composer(input, setInput, () => send(), `Escreva sua dúvida para o ${info.displayName}…`)}
      </>
    );
  }

  // ---------- Conversas anteriores ----------
  if (view === "history") {
    return (
      <>
        {tabs}
        <AuditNotice clinicName={user?.clinicName} />
        <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
          <Box
            onClick={startNew}
            sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, borderRadius: "10px", cursor: "pointer", color: colors.primary, bgcolor: "#eef7f8", "&:hover": { bgcolor: "#dff1f3" } }}
          >
            <Add fontSize="small" />
            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>Nova conversa</Typography>
          </Box>
          {history.length === 0 ? (
            <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: "center", py: 3 }}>
              Nenhuma conversa ainda.
            </Typography>
          ) : (
            history.map((c) => (
              <Box
                key={c.id}
                onClick={() => openConversation(c.id)}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, p: 1, borderRadius: "8px", cursor: "pointer", "&:hover": { bgcolor: "#f1f3f5" } }}
              >
                <ChatBubbleOutline sx={{ fontSize: 20, color: colors.textSecondary, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: "0.85rem", color: colors.textPrimary }} noWrap>
                    {c.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {formatWhen(c.lastMessageAt)}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </>
    );
  }

  // ---------- Abrir chamado ----------
  if (view === "newTicket") {
    const canSend = newSubject.trim().length > 0 && newDescription.trim().length > 0 && !sending;
    return (
      <>
        {tabs}
        <Box sx={{ flex: 1, overflowY: "auto", p: 1.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: colors.textPrimary }}>Abrir chamado</Typography>
          <Typography sx={{ fontSize: "0.8rem", color: colors.textSecondary }}>
            A equipe de Suporte Clinic4Us responde em até 24 horas úteis. Não inclua dados de saúde, CPF ou documentos de pacientes.
          </Typography>
          <TextField label="Assunto" size="small" value={newSubject} onChange={(e) => setNewSubject(e.target.value)} inputProps={{ maxLength: 120 }} required />
          <TextField
            select
            label="Tipo"
            size="small"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value as SupportTicketCategory)}
          >
            {(Object.keys(TICKET_CATEGORY_LABEL) as SupportTicketCategory[]).map((c) => (
              <MenuItem key={c} value={c}>
                {TICKET_CATEGORY_LABEL[c]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="O que aconteceu?"
            size="small"
            multiline
            minRows={4}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
            inputProps={{ maxLength: 4000 }}
            helperText="Conte em que tela você estava, o que tentou fazer e a mensagem que apareceu."
            required
          />
          {error && <Typography variant="caption" sx={{ color: colors.error }}>{error}</Typography>}
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={() => setView("tickets")} sx={{ textTransform: "none" }}>
              Cancelar
            </Button>
            <Button variant="contained" disabled={!canSend} onClick={createTicket} sx={{ textTransform: "none", bgcolor: colors.primary, "&:hover": { bgcolor: "#029AAB" } }}>
              {sending ? "Enviando…" : "Abrir chamado"}
            </Button>
          </Box>
        </Box>
      </>
    );
  }

  // ---------- Detalhe do chamado ----------
  if (view === "ticket") {
    return (
      <>
        {tabs}
        <Box sx={{ flex: 1, overflowY: "auto", px: 2, py: 1.5, bgcolor: colors.white, display: "flex", flexDirection: "column", gap: 2.5 }}>
          {!ticket ? (
            error ? (
              <Typography variant="caption" sx={{ color: colors.error, textAlign: "center" }}>{error}</Typography>
            ) : (
              <CircularProgress size={20} sx={{ alignSelf: "center", color: colors.primary, mt: 3 }} />
            )
          ) : (
            <>
              <Box sx={{ p: 1.25, bgcolor: "#fff", border: "1px solid #e9ecef", borderRadius: "10px" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Typography sx={{ fontWeight: 700, fontSize: "0.9rem" }}>#{ticket.ticket.number}</Typography>
                  {statusChip(ticket.ticket.status)}
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {TICKET_CATEGORY_LABEL[ticket.ticket.category]}
                  </Typography>
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.88rem", mt: 0.5 }}>{ticket.ticket.subject}</Typography>
                <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                  Aberto em {formatWhen(ticket.ticket.createdAt)} · resposta em até 24 horas úteis
                </Typography>
              </Box>
              {ticket.messages.map((m) =>
                bubble(
                  m.fromSupport ? "assistant" : "user",
                  <>
                    <Box sx={{ whiteSpace: "pre-wrap" }}>{m.body}</Box>
                    <Typography sx={{ fontSize: "0.75rem", color: colors.textSecondary, mt: 0.5 }}>{formatWhen(m.createdAt)}</Typography>
                  </>,
                  m.id,
                  undefined,
                  m.fromSupport ? m.authorName : "Você"
                )
              )}
              {error && <Typography variant="caption" sx={{ color: colors.error, textAlign: "center" }}>{error}</Typography>}
            </>
          )}
        </Box>
        {ticket && composer(reply, setReply, sendReply, "Responder ao Suporte Clinic4Us…")}
      </>
    );
  }

  // ---------- Meus chamados ----------
  return (
    <>
      {tabs}
      <Box sx={{ flex: 1, overflowY: "auto", p: 1 }}>
        <Box
          onClick={() => {
            setError(null);
            setView("newTicket");
          }}
          sx={{ display: "flex", alignItems: "center", gap: 1, p: 1.25, borderRadius: "10px", cursor: "pointer", color: colors.primary, bgcolor: "#eef7f8", "&:hover": { bgcolor: "#dff1f3" } }}
        >
          <Add fontSize="small" />
          <Typography sx={{ fontWeight: 600, fontSize: "0.9rem" }}>Abrir chamado</Typography>
        </Box>
        {ticketsLoading && tickets.length === 0 ? (
          <CircularProgress size={20} sx={{ display: "block", mx: "auto", mt: 3, color: colors.primary }} />
        ) : tickets.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.textSecondary, textAlign: "center", py: 3 }}>
            Você ainda não abriu chamados.
          </Typography>
        ) : (
          tickets.map((t) => (
            <Box
              key={t.id}
              onClick={() => openTicket(t.id)}
              sx={{ display: "flex", alignItems: "center", gap: 1.25, p: 1, borderRadius: "8px", cursor: "pointer", "&:hover": { bgcolor: "#f1f3f5" } }}
            >
              <ConfirmationNumberOutlined sx={{ fontSize: 20, color: colors.textSecondary, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography sx={{ fontSize: "0.85rem", fontWeight: t.unread ? 700 : 500, color: colors.textPrimary }} noWrap>
                    #{t.number} · {t.subject}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.25 }}>
                  {statusChip(t.status)}
                  <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                    {formatWhen(t.lastActivityAt)}
                  </Typography>
                </Box>
              </Box>
              {t.unread && (
                <Box
                  aria-label="Resposta nova"
                  sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: colors.primary, flexShrink: 0 }}
                />
              )}
            </Box>
          ))
        )}
      </Box>
    </>
  );
});

SupportConversation.displayName = "SupportConversation";

export default SupportConversation;
